import { CheckmarkOutlined, DeleteOutlined, EditOutlined } from '@icon';
import { MenuItemType, Table } from '@ui';
import dayjs from 'dayjs';
import React from 'react';
import { useIntl } from 'react-intl';
import { getShortDateFormat, getTimeFormat } from '../../../../helpers/utils-helpers';
import StaffStateTag from './components/staff-state-tag';

export interface IStaffPaginationArgs {
  page: number;
  pageSize: number;
  sortBy?: 'lastName' | 'createdAt';
  direction?: 'ASC' | 'DESC';
}

export interface IStaffPagination extends IStaffPaginationArgs {
  page: number;
  total: number;
  current: number;
}
interface GetTableColumnParams {
  onApproveClick: (rowValues: any) => void;
  onEditClick: (rowValues: any) => void;
  onDeleteClick: (rowValues: any) => void;
  canEdit?: boolean;
}

export function useTableColumns(params: GetTableColumnParams) {
  const { formatMessage } = useIntl();
  const { onDeleteClick, onApproveClick, onEditClick, canEdit } = params;

  return React.useMemo(
    () => [
      {
        Header: formatMessage({ id: 'staff_table_name' }),
        id: 'lastName',
        sortType: 'basic',
        accessor: 'lastName',
        disableSortBy: false,
        Cell: ({ row }: any) => {
          if (!row.original.user.firstName) {
            return null;
          }

          return (
            <span>
              {row.original.user.firstName} {row.original.user.lastName}
            </span>
          );
        },
      },
      {
        Header: formatMessage({ id: 'staff_table_role' }),
        accessor: 'role',
        id: 'role',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          return (
            <span>{formatMessage({ id: `business_staff_role_${row.original.role.name}` })}</span>
          );
        },
      },
      {
        Header: formatMessage({ id: 'staff_table_state' }),
        accessor: 'status',
        id: 'state',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          return <StaffStateTag state={row.original.status} />;
        },
      },
      {
        Header: formatMessage({ id: 'staff_table_created' }),
        accessor: 'createdAt',
        id: 'createdAt',
        disableSortBy: false,
        Cell: ({ row }: any) => {
          return (
            <span>
              {dayjs(row.original.createdAt).format(`${getShortDateFormat()} ${getTimeFormat()}`)}
            </span>
          );
        },
      },
      {
        Header: '',
        id: 'edit',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          if (
            (row.original.status !== 'pending' && row.original.status !== 'approved') ||
            !canEdit
          ) {
            return <></>;
          }

          const menuItems: MenuItemType[] = [
            // TODO: enable in V2
            // {
            //   key: 'delete',
            //   variant: 'danger',
            //   label: formatMessage({ id: 'table_context_menu_remove' }),
            //   Icon: DeleteOutlined,
            //   onClick: () => onDeleteClick(row.original),
            //   disabled: true,
            // },
          ];

          if (row.original.status === 'pending') {
            // menuItems.unshift({ type: 'divider' });
            menuItems.unshift({
              key: 'approve',
              label: formatMessage({ id: 'table_context_menu_approve' }),
              Icon: CheckmarkOutlined,
              onClick: () => onApproveClick(row.original),
              disabled: row.original.status !== 'pending',
            });
          } else if (row.original.status === 'approved') {
            // menuItems.unshift({ type: 'divider' });
            menuItems.unshift({
              key: 'edit',
              label: formatMessage({ id: 'table_context_menu_edit' }),
              Icon: EditOutlined,
              onClick: () => onEditClick(row.original),
              disabled: row.original.status !== 'approved',
            });
          }

          return <Table.Actions items={menuItems} />;
        },
      },
    ],
    [formatMessage, onApproveClick, onEditClick, onDeleteClick, canEdit],
  );
}
